import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router';
import { useAuthStore } from '@/stores/auth';

const routes: RouteRecordRaw[] = [
  {
    path: '/login',
    name: 'login',
    component: () => import('@/views/LoginView.vue'),
    meta: { public: true },
  },
  {
    path: '/',
    component: () => import('@/components/AppShell.vue'),
    children: [
      {
        path: '',
        name: 'home',
        component: () => import('@/views/HomeView.vue'),
      },
      {
        path: 'faq',
        name: 'faq',
        component: () => import('@/views/FaqView.vue'),
      },
      {
        path: 'redirects',
        name: 'redirects',
        component: () => import('@/views/RedirectionView.vue'),
      },
      {
        path: 'logs',
        name: 'logs',
        component: () => import('@/views/LogsView.vue'),
      },
      {
        path: 'coupons',
        name: 'coupons',
        component: () => import('@/views/CouponsView.vue'),
      },
      {
        path: 'users',
        name: 'users',
        component: () => import('@/views/UsersView.vue'),
      },
      {
        path: 'users/new',
        name: 'user-new',
        component: () => import('@/views/UserEditorView.vue'),
      },
      {
        path: 'users/:userId(\\d+)',
        name: 'user-editor',
        component: () => import('@/views/UserEditorView.vue'),
      },
      {
        path: 'product-announcements',
        name: 'product-announcements',
        component: () => import('@/views/ProductAnnouncementsView.vue'),
      },
      {
        path: 'roles',
        name: 'roles',
        component: () => import('@/views/RolesView.vue'),
      },
      {
        path: 'permissions',
        name: 'permissions',
        component: () => import('@/views/PermissionsView.vue'),
      },
      {
        path: 'products',
        name: 'products',
        component: () => import('@/views/ProductsView.vue'),
      },
      {
        path: 'products/new',
        redirect: { name: 'product-new-section', params: { section: 'basics' } },
      },
      {
        path: 'products/new/:section',
        name: 'product-new-section',
        component: () => import('@/views/ProductEditorView.vue'),
      },
      {
        path: 'products/:id',
        redirect: (to) => ({
          name: 'product-editor-section',
          params: { id: to.params.id, section: 'overview' },
        }),
      },
      {
        path: 'products/:id/:section',
        name: 'product-editor-section',
        component: () => import('@/views/ProductEditorView.vue'),
      },
      {
        path: 'courses',
        name: 'courses',
        component: () => import('@/views/CoursesView.vue'),
      },
      {
        path: 'courses/:id',
        name: 'course-editor',
        component: () => import('@/views/CourseEditorView.vue'),
      },
      {
        path: 'certificates',
        name: 'certificates',
        component: () => import('@/views/CertificatesView.vue'),
      },
      {
        path: 'certificates/:id',
        name: 'certificate-editor',
        component: () => import('@/views/CertificateEditorView.vue'),
      },
      {
        path: 'reports',
        name: 'reports',
        component: () => import('@/views/ReportsView.vue'),
      },
      {
        path: 'reports/:id',
        name: 'report-editor',
        component: () => import('@/views/ReportEditorView.vue'),
      },
      {
        path: 'reports/:id/run',
        name: 'report-results',
        component: () => import('@/views/ReportResultsView.vue'),
      },
      {
        path: 'scheduled-reports',
        name: 'scheduled-reports',
        component: () => import('@/views/ScheduledReportsView.vue'),
      },
      {
        path: 'custom-fields',
        name: 'custom-fields',
        component: () => import('@/views/CustomFieldsView.vue'),
      },
      {
        path: 'groups',
        name: 'groups',
        component: () => import('@/views/GroupsView.vue'),
      },
      {
        path: 'categories',
        name: 'categories',
        component: () => import('@/views/CategoriesView.vue'),
      },
      {
        path: 'test-form-report',
        name: 'test-form-report',
        component: () => import('@/views/TestFormReportView.vue'),
      },
      {
        path: 'cover-letter',
        name: 'cover-letter',
        component: () => import('@/views/CoverLetterView.vue'),
      },
      {
        path: 'proctor-auditor',
        name: 'proctor-auditor',
        component: () => import('@/views/ProctorAuditorView.vue'),
      },
      {
        path: 'voucher-lookup',
        name: 'voucher-lookup',
        component: () => import('@/views/VoucherLookupView.vue'),
      },
      {
        path: 'vouchers',
        name: 'vouchers',
        component: () => import('@/views/PurchasedVouchersView.vue'),
      },
      {
        path: 'used-vouchers',
        name: 'used-vouchers',
        component: () => import('@/views/UsedVouchersView.vue'),
      },
      {
        path: 'upsells',
        name: 'upsells',
        component: () => import('@/views/UpsellConfigurationView.vue'),
      },
      {
        path: 'reviews',
        name: 'product-reviews',
        component: () => import('@/views/ProductReviewManagerView.vue'),
      },
      {
        path: 'custom-portals',
        name: 'custom-portals',
        component: () => import('@/views/CustomPortalsView.vue'),
      },
      {
        path: 'custom-portals/new',
        name: 'custom-portal-new',
        component: () => import('@/views/CustomPortalEditorView.vue'),
      },
      {
        path: 'custom-portals/:id',
        name: 'custom-portal-editor',
        component: () => import('@/views/CustomPortalEditorView.vue'),
      },
      {
        path: 'proctor-tools',
        name: 'proctor-tools',
        component: () => import('@/views/ProctorToolsView.vue'),
      },
      {
        path: 'banner-announcement',
        name: 'banner-announcement',
        component: () => import('@/views/BannerAnnouncementView.vue'),
      },
      {
        path: 'gallery',
        name: 'gallery',
        component: () => import('@/views/GalleryManagerView.vue'),
      },
      {
        path: 'uploaded-images',
        name: 'uploaded-images',
        component: () => import('@/views/UploadedImageManagerView.vue'),
      },
      {
        path: 'api-credentials',
        name: 'api-credentials',
        component: () => import('@/views/ApiCredentialsView.vue'),
      },
      {
        path: 'third-party-certificate-tracker',
        name: 'third-party-certificate-tracker',
        component: () => import('@/views/ThirdPartyCertificateTrackerView.vue'),
      },
      {
        path: 'group-certificate-access',
        name: 'group-certificate-access',
        component: () => import('@/views/GroupCertificateAccessView.vue'),
      },
      {
        path: 'integrations',
        name: 'integrations',
        component: () => import('@/views/IntegrationsView.vue'),
      },
      {
        path: 'integrations/new',
        name: 'integration-new',
        component: () => import('@/views/IntegrationEditorView.vue'),
      },
      {
        path: 'integrations/:id',
        name: 'integration-editor',
        component: () => import('@/views/IntegrationEditorView.vue'),
      },
    ],
  },
];

export const router = createRouter({
  // The app is mounted at /admin/ in production; Vite's `base` adds the
  // prefix automatically so we just need web history here.
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
});

// Route guard: if not authenticated, bounce to /login.
router.beforeEach(async (to) => {
  if (to.meta.public) return true;

  const auth = useAuthStore();
  if (auth.isAuthenticated) return true;

  // First-time load — check for an existing session cookie.
  const ok = await auth.checkSession();
  if (ok) return true;

  return { name: 'login', query: { redirect: to.fullPath } };
});
